import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Building2, Store, Mail, Phone, Globe, MapPin, Clock, Tag, Share2,
  Loader2, CheckCircle, ArrowRight, ArrowLeft, Upload, X, CalendarDays, Heart
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { normalizeUrl, normalizeUrlFields } from "@/lib/urlUtils";

import { HoursSelector } from "@/components/HoursSelector";
import { DatePopover } from "@/components/DatePopover";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_BG_IMAGE = "https://customer-assets.emergentagent.com/job_biz-directory-27/artifacts/miycx0rf_614shoplocal-background.png";

const getLogoUrl = (logo) => {
  if (!logo) return null;
  if (logo.startsWith("http")) return logo;
  return `${BACKEND_URL}${logo}`;
};

const BUSINESS_TYPE_OPTIONS = ["Goods", "Services", "Food & Beverage", "Nonprofits"];
const BUSINESS_SUBCATEGORY_OPTIONS = {
  Goods: ["Art", "Books", "Candles", "Clothing", "Home Goods", "Jewelry", "Plants", "Skincare", "Vintage"],
  Services: ["Beauty", "Creative Services", "Education", "Event Services", "Fitness & Wellness", "Home Services", "Pet Services", "Photography", "Professional Services", "Repair Services"],
  "Food & Beverage": ["Bakery", "Beverages", "Catering", "Coffee & Tea", "Desserts", "Food Trucks", "Prepared Foods", "Specialty Foods", "Snacks"],
  Nonprofits: ["Chambers of Commerce"],
};
const LOCATION_OPTIONS = [
  "Clintonville", "Columbus", "East Market", "German Village", "Hilliard",
  "Linden", "New Albany", "Reynoldsburg", "Short North", "Westerville", "Worthington",
  "Outside of the Greater Columbus Area",
];

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const [regType, setRegType] = useState("business");
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  const [email, setEmail] = useState("");

  // Business fields
  const [biz, setBiz] = useState({
    business_name: "", business_type: "", business_categories: [], business_category_other: "",
    instagram: "", facebook: "", tiktok: "",
    address: "", is_home_based: false, website: "", hours: {}, business_description: "",
    location: "", location_other: "", phone_number: "", logo: ""
  });

  // Market fields
  const [mkt, setMkt] = useState({
    market_name: "", instagram: "", facebook: "", tiktok: "", website: "",
    season_start: "", season_end: "", season_year_round: false,
    hours: {}, hours_event_based: false,
    address: "", logo: "", description: ""
  });

  const isBusiness = regType === "business";
  const isMarket = regType === "market";
  const isShopper = regType === "shopper";
  const currentName = isBusiness ? biz.business_name : mkt.market_name;
  const currentLogo = isBusiness ? biz.logo : mkt.logo;

  const handleBizChange = (f, v) => setBiz((p) => ({ ...p, [f]: v }));
  const handleMktChange = (f, v) => setMkt((p) => ({ ...p, [f]: v }));

  const handleBusinessTypeChange = (value) => {
    setBiz((p) => ({ ...p, business_type: value, business_categories: [], business_category_other: "" }));
  };

  const toggleBusinessCategory = (category) => {
    setBiz((p) => {
      const current = p.business_categories || [];
      const next = current.includes(category)
        ? current.filter((item) => item !== category)
        : [...current, category];
      return {
        ...p,
        business_categories: next,
        business_category_other: next.includes("Other") ? p.business_category_other : "",
      };
    });
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) { toast.error("Invalid file type."); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("File too large. Max 2MB."); return; }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const endpoint = isBusiness ? `${API}/register/upload-logo` : `${API}/markets/upload-logo`;
      const response = await axios.post(endpoint, formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (response.data.success) {
        if (isBusiness) setBiz((p) => ({ ...p, logo: response.data.logo_url }));
        else setMkt((p) => ({ ...p, logo: response.data.logo_url }));
        toast.success("Logo uploaded");
      }
    } catch { toast.error("Failed to upload logo."); }
    finally { setUploadingLogo(false); }
  };

  const handleRemoveLogo = () => {
    if (isBusiness) setBiz((p) => ({ ...p, logo: "" }));
    else setMkt((p) => ({ ...p, logo: "" }));
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) { toast.error("Please enter a valid email"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/register/check-email`, { email });
      if (response.data.exists) { toast.error("This email is already registered. Please login instead."); return; }
      if (isShopper) {
        navigate(`/shopper-signup?email=${encodeURIComponent(email)}`);
        return;
      }
      setStep(2);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to verify email"); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (isBusiness) {
      if (!biz.business_name) { toast.error("Business name is required"); return; }
      const selectedLocation = biz.location === "Other" ? biz.location_other.trim() : biz.location;
      if (biz.location === "Other" && !selectedLocation) {
        toast.error("Please enter your city or region");
        return;
      }
      setLoading(true);
      try {
        const { location_other, ...businessFields } = biz;
        const r = await axios.post(`${API}/register/business`, normalizeUrlFields({
          ...businessFields,
          location: selectedLocation,
          business_locations: selectedLocation ? [selectedLocation] : [],
          business_email: email,
        }));
        if (r.data?.token) login(r.data.token, r.data.email, "business");
        navigate("/dashboard");
      } catch (error) { toast.error(error.response?.data?.detail || "Registration failed"); }
      finally { setLoading(false); }
    } else {
      if (!mkt.market_name) { toast.error("Market name is required"); return; }
      setLoading(true);
      try {
        const r = await axios.post(`${API}/markets/register`, normalizeUrlFields({ ...mkt, email }));
        if (r.data?.token) login(r.data.token, r.data.email, "market");
        navigate("/market-dashboard");
      } catch (error) { toast.error(error.response?.data?.detail || "Registration failed"); }
      finally { setLoading(false); }
    }
  };

  // Step 3: Success
  if (step === 3) {
    return (
      <div className="page-fade-in py-20 px-6">
        <div className="max-w-lg mx-auto text-center">
          <div className="w-20 h-20 bg-[#42D78A]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#42D78A]" />
          </div>
          <h1 className="text-3xl font-bold text-[#1a1a1a] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Registration Submitted!
          </h1>
          <p className="text-gray-600 mb-8" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Your {isBusiness ? "business" : "market"} listing is pending approval. We'll email <span className="font-medium">{email}</span> once approved.
          </p>
          <div className="flex flex-col gap-3">
            <Button onClick={() => navigate("/")} className="bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full py-5 font-bold" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="back-to-home-btn">
              Back to Home
            </Button>
            <Button variant="outline" onClick={() => navigate("/login")} className="rounded-full py-5 border-2 border-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white font-bold" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="go-to-login-btn">
              Login to Your Account
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-fade-in">
      {/* Hero */}
      <section className="bg-[#EEF8F2] text-[#1a1a1a] border-b border-[#42D78A]/20 py-12 md:py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#42D78A] text-sm tracking-[0.2em] uppercase mb-3 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>Register</p>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Join 614 Shop Local
          </h1>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Progress */}
        <div className="flex items-center justify-center gap-4 mb-10">
          <div className={`flex items-center gap-2 ${step >= 1 ? "text-[#1a1a1a]" : "text-gray-400"}`}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? "bg-[#42D78A] text-[#1a1a1a]" : "bg-gray-200 text-gray-500"}`}>1</div>
            <span className="text-sm font-medium hidden sm:inline" style={{ fontFamily: "'Outfit', sans-serif" }}>Type & Email</span>
          </div>
          <div className="w-12 h-0.5 bg-gray-200"><div className={`h-full transition-all ${step >= 2 ? "bg-[#42D78A] w-full" : "w-0"}`} /></div>
          <div className={`flex items-center gap-2 ${step >= 2 ? "text-[#1a1a1a]" : "text-gray-400"}`}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? "bg-[#42D78A] text-[#1a1a1a]" : "bg-gray-200 text-gray-500"}`}>2</div>
            <span className="text-sm font-medium hidden sm:inline" style={{ fontFamily: "'Outfit', sans-serif" }}>Details</span>
          </div>
        </div>

        {/* Step 1: Type & Email */}
        {step === 1 && (
          <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>What are you registering?</h2>
              <p className="text-gray-500 mt-2 text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>Choose your account type and enter your email</p>
            </div>

            {/* Type Selector */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              <button
                type="button"
                onClick={() => setRegType("business")}
                className={`flex flex-col items-center gap-2 p-5 rounded-xl border-2 transition-all ${
                  isBusiness
                    ? "border-[#42D78A] bg-[#42D78A]/5"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                data-testid="register-type-business"
              >
                <Building2 className={`w-7 h-7 ${isBusiness ? "text-[#42D78A]" : "text-gray-400"}`} />
                <span className={`text-sm font-bold ${isBusiness ? "text-[#1a1a1a]" : "text-gray-500"}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Business
                </span>
                <span className="text-xs text-gray-400" style={{ fontFamily: "'Montserrat', sans-serif" }}>Free business directory listing</span>
              </button>
              <button
                type="button"
                onClick={() => setRegType("market")}
                className={`flex flex-col items-center gap-2 p-5 rounded-xl border-2 transition-all ${
                  isMarket
                    ? "border-[#42D78A] bg-[#42D78A]/5"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                data-testid="register-type-market"
              >
                <Store className={`w-7 h-7 ${isMarket ? "text-[#42D78A]" : "text-gray-400"}`} />
                <span className={`text-sm font-bold ${isMarket ? "text-[#1a1a1a]" : "text-gray-500"}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Market
                </span>
                <span className="text-xs text-gray-400" style={{ fontFamily: "'Montserrat', sans-serif" }}>Free market registration</span>
              </button>
              <button
                type="button"
                onClick={() => setRegType("shopper")}
                className={`flex flex-col items-center gap-2 p-5 rounded-xl border-2 transition-all ${
                  isShopper
                    ? "border-[#42D78A] bg-[#42D78A]/5"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                data-testid="register-type-shopper"
              >
                <Heart className={`w-7 h-7 ${isShopper ? "text-[#42D78A]" : "text-gray-400"}`} />
                <span className={`text-sm font-bold ${isShopper ? "text-[#1a1a1a]" : "text-gray-500"}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Shopper
                </span>
                <span className="text-xs text-gray-400" style={{ fontFamily: "'Montserrat', sans-serif" }}>Free shopper profile</span>
              </button>
            </div>

            <div className="mb-6 rounded-2xl border border-gray-100 bg-gray-50 p-4">
              {isBusiness && (
                <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <span className="font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Business listings are free:</span>{" "}
                  Set up your business profile for FREE.
                </p>
              )}
              {isMarket && (
                <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <span className="font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Markets register for free.</span>{" "}
                  Add your market details so shoppers can find your schedule, location, and upcoming market days.
                </p>
              )}
              {isShopper && (
                <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <span className="font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Shopper profiles are free.</span>{" "}
                  Create a profile to save businesses and follow markets.
                </p>
              )}
            </div>

            <form onSubmit={handleEmailSubmit} className="space-y-6">
              <div>
                <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Email *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={isBusiness ? "you@business.com" : isMarket ? "you@market.com" : "you@example.com"} className="h-12 pl-12 rounded-lg" data-testid="register-email-input" disabled={loading} />
                </div>
              </div>
              <Button type="submit" className="w-full h-12 bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full font-bold" disabled={loading} data-testid="register-continue-btn" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking...</> : <>Continue <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Already registered? <a href="/login" className="text-[#1a1a1a] font-medium hover:text-[#42D78A]">Login here</a>
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <div className="space-y-6">
            <Button variant="ghost" onClick={() => setStep(1)} className="text-gray-600 hover:text-[#1a1a1a] -ml-2">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>

            {/* Logo & Name (shared) */}
            <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm">
              <h3 className="text-lg font-bold text-[#1a1a1a] mb-5 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {isBusiness ? <Building2 className="w-5 h-5 text-[#42D78A]" /> : <Store className="w-5 h-5 text-[#42D78A]" />}
                {isBusiness ? "Business Identity" : "Market Identity"}
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>{isBusiness ? "Business Name" : "Market Name"} *</Label>
                  <Input
                    value={currentName}
                    onChange={(e) => isBusiness ? handleBizChange("business_name", e.target.value) : handleMktChange("market_name", e.target.value)}
                    placeholder={isBusiness ? "Your Business Name" : "Your Market Name"}
                    className="h-11 mt-1 rounded-lg"
                    data-testid="register-name"
                  />
                </div>
                {/* Logo Upload */}
                <div>
                  <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Logo</Label>
                  <p className="text-xs text-gray-500 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>Upload your logo (optional)</p>
                  <div className="relative flex items-center justify-center border border-gray-200 rounded-xl overflow-hidden"
                    style={{ width: "100%", maxWidth: "465px", height: "0", paddingBottom: "64.5%", backgroundImage: `url(${DEFAULT_BG_IMAGE})`, backgroundSize: "cover", backgroundPosition: "center" }}>
                    {currentLogo ? (
                      <div className="absolute inset-0 flex items-center justify-center p-6 bg-white">
                        <img src={getLogoUrl(currentLogo)} alt="Logo preview" className="max-h-full max-w-full object-contain" style={{ maxHeight: "80%", maxWidth: "80%" }} />
                        <button onClick={handleRemoveLogo} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600" type="button"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center p-4">
                        <span className="text-lg font-bold text-center text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>{currentName || (isBusiness ? "Your Business Name" : "Your Market Name")}</span>
                      </div>
                    )}
                    {uploadingLogo && <div className="absolute inset-0 bg-white/80 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-gray-600" /></div>}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleLogoUpload} className="hidden" />
                  <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploadingLogo} className="w-full mt-3 max-w-[465px] rounded-full">
                    <Upload className="w-4 h-4 mr-2" /> {uploadingLogo ? "Uploading..." : currentLogo ? "Change Logo" : "Upload Logo"}
                  </Button>
                </div>

                {/* Business-only: Description */}
                {isBusiness && (
                  <div>
                    <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Description</Label>
                    <Textarea value={biz.business_description} onChange={(e) => handleBizChange("business_description", e.target.value)} placeholder="Tell us about your business..." className="mt-1 min-h-[100px] rounded-lg" data-testid="register-description" />
                  </div>
                )}
                {/* Market-only: Website */}
                {!isBusiness && (
                  <div>
                    <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Website</Label>
                    <Input type="url" value={mkt.website} onChange={(e) => handleMktChange("website", e.target.value)} onBlur={(e) => handleMktChange("website", normalizeUrl(e.target.value))} placeholder="https://yourmarket.com" className="h-11 mt-1 rounded-lg" data-testid="register-website" />
                  </div>
                )}
              </div>
            </div>

            {/* Business: Classification */}
            {isBusiness && (
              <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm">
                <h3 className="text-lg font-bold text-[#1a1a1a] mb-5 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  <Tag className="w-5 h-5 text-[#42D78A]" /> Classification
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Main Category</Label>
                    <Select value={biz.business_type} onValueChange={handleBusinessTypeChange}>
                      <SelectTrigger className="h-11 mt-1 rounded-lg" data-testid="register-type"><SelectValue placeholder="Choose a main category" /></SelectTrigger>
                      <SelectContent>{BUSINESS_TYPE_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Subcategories</Label>
                    <div className="mt-2 rounded-xl border border-gray-200 p-3">
                      {!biz.business_type ? (
                        <p className="text-sm text-gray-400" style={{ fontFamily: "'Montserrat', sans-serif" }}>Choose a main category first.</p>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {[...(BUSINESS_SUBCATEGORY_OPTIONS[biz.business_type] || []), "Other"].map((category) => (
                            <label key={category} className="flex items-center gap-2 text-sm text-gray-600" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                              <Checkbox
                                checked={(biz.business_categories || []).includes(category)}
                                onCheckedChange={() => toggleBusinessCategory(category)}
                                data-testid={`register-subcategory-${category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                              />
                              {category}
                            </label>
                          ))}
                        </div>
                      )}
                      {(biz.business_categories || []).includes("Other") && (
                        <Input
                          value={biz.business_category_other}
                          onChange={(e) => handleBizChange("business_category_other", e.target.value)}
                          placeholder="Add another category"
                          className="h-11 mt-3 rounded-lg"
                          data-testid="register-subcategory-other"
                        />
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Location</Label>
                    <Select value={biz.location} onValueChange={(v) => handleBizChange("location", v)}>
                      <SelectTrigger className="h-11 mt-1 rounded-lg" data-testid="register-location"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {LOCATION_OPTIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {biz.location === "Other" && (
                      <Input
                        value={biz.location_other}
                        onChange={(e) => handleBizChange("location_other", e.target.value)}
                        placeholder="Enter another city or region"
                        className="h-11 mt-2 rounded-lg"
                        data-testid="register-location-other"
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Market: Schedule & Location */}
            {!isBusiness && (
              <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm">
                <h3 className="text-lg font-bold text-[#1a1a1a] mb-5 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  <CalendarDays className="w-5 h-5 text-[#42D78A]" /> Schedule & Location
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-4 rounded-xl bg-gray-50 p-4">
                    <div>
                      <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Year-round market</Label>
                      <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>Select this if your market runs all year.</p>
                    </div>
                    <Switch
                      checked={mkt.season_year_round}
                      onCheckedChange={(checked) => handleMktChange("season_year_round", checked)}
                      data-testid="register-season-year-round"
                    />
                  </div>
                  {!mkt.season_year_round && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Season Start</Label>
                        <DatePopover value={mkt.season_start} onChange={(v) => handleMktChange("season_start", v)} testId="register-season-start" placeholder="Pick start date" />
                      </div>
                      <div>
                        <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Season End</Label>
                        <DatePopover value={mkt.season_end} onChange={(v) => handleMktChange("season_end", v)} testId="register-season-end" placeholder="Pick end date" />
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-4 rounded-xl bg-gray-50 p-4">
                    <div>
                      <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Event based hours</Label>
                      <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>Use events for specific dates and times after your account is approved.</p>
                    </div>
                    <Switch
                      checked={mkt.hours_event_based}
                      onCheckedChange={(checked) => handleMktChange("hours_event_based", checked)}
                      data-testid="register-hours-event-based"
                    />
                  </div>
                  {!mkt.hours_event_based && (
                    <div>
                      <Label className="text-[#1a1a1a] font-medium text-sm mb-2 block" style={{ fontFamily: "'Outfit', sans-serif" }}>Operating Hours</Label>
                      <HoursSelector value={mkt.hours} onChange={(v) => handleMktChange("hours", v)} />
                    </div>
                  )}
                  <div>
                    <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Address</Label>
                    <Input value={mkt.address} onChange={(e) => handleMktChange("address", e.target.value)} placeholder="123 Main St, Columbus, OH" className="h-11 mt-1 rounded-lg" data-testid="register-market-address" />
                  </div>
                </div>
              </div>
            )}

            {/* Business: Contact */}
            {isBusiness && (
              <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm">
                <h3 className="text-lg font-bold text-[#1a1a1a] mb-5 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  <Phone className="w-5 h-5 text-[#42D78A]" /> Contact Information
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Phone</Label>
                      <Input value={biz.phone_number} onChange={(e) => handleBizChange("phone_number", e.target.value)} placeholder="+1 (555) 123-4567" className="h-11 mt-1 rounded-lg" data-testid="register-phone" />
                    </div>
                    <div>
                      <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Website</Label>
                      <Input type="url" value={biz.website} onChange={(e) => handleBizChange("website", e.target.value)} onBlur={(e) => handleBizChange("website", normalizeUrl(e.target.value))} placeholder="https://yourbusiness.com" className="h-11 mt-1 rounded-lg" data-testid="register-biz-website" />
                    </div>
                  </div>
                  <div>
                    <label className="flex items-start gap-3 rounded-xl bg-gray-50 p-3 text-sm text-gray-600" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      <Checkbox
                        checked={biz.is_home_based}
                        onCheckedChange={(checked) => handleBizChange("is_home_based", checked === true)}
                        data-testid="register-home-based"
                      />
                      <span>
                        <span className="font-semibold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>I am a home-based business.</span>{" "}
                        Do not show my physical address in the public directory.
                      </span>
                    </label>
                    {!biz.is_home_based && (
                      <div className="mt-3">
                        <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Address</Label>
                        <Textarea value={biz.address} onChange={(e) => handleBizChange("address", e.target.value)} placeholder="123 Main Street" className="mt-1 rounded-lg" data-testid="register-address" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Market: Social Media & Description */}
            {!isBusiness && (
              <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm">
                <h3 className="text-lg font-bold text-[#1a1a1a] mb-5 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  <Share2 className="w-5 h-5 text-[#42D78A]" /> Social Media & Description
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Instagram</Label>
                      <Input value={mkt.instagram} onChange={(e) => handleMktChange("instagram", e.target.value)} placeholder="https://instagram.com/yourmarket" className="h-11 mt-1 rounded-lg" data-testid="register-instagram" />
                    </div>
                    <div>
                      <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Facebook</Label>
                      <Input value={mkt.facebook} onChange={(e) => handleMktChange("facebook", e.target.value)} placeholder="https://facebook.com/yourmarket" className="h-11 mt-1 rounded-lg" data-testid="register-facebook" />
                    </div>
                    <div>
                      <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>TikTok</Label>
                      <Input value={mkt.tiktok} onChange={(e) => handleMktChange("tiktok", e.target.value)} placeholder="https://tiktok.com/@yourmarket" className="h-11 mt-1 rounded-lg" data-testid="register-tiktok" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#1a1a1a] font-medium text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Description</Label>
                    <Textarea
                      value={mkt.description}
                      onChange={(e) => handleMktChange("description", e.target.value)}
                      placeholder="Tell shoppers about your market..."
                      className="mt-1 min-h-[100px] rounded-lg"
                      data-testid="register-description-market"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Submit */}
            <div className="flex gap-4">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1 h-12 rounded-full border-2 font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>Back</Button>
              <Button onClick={handleSubmit} disabled={loading} className="flex-1 h-12 bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full font-bold" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="register-submit-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : "Submit for Approval"}
              </Button>
            </div>
            <p className="text-center text-sm text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Your {isBusiness ? "business" : "market"} will be reviewed within 24-48 hours.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisterPage;
